import { useEffect, useState } from "react";
import { type ClienteNexo, type ResultadoCruce, vizCruce } from "../api.ts";
import { C, autoridadEs, etiquetaListaSat, fecha, st } from "../theme.ts";
import { Cargando, ErrorBox, celdaTd, celdaTh } from "../ui/componentes.tsx";
import { rutaAHash } from "../rutas.ts";

const METODO_ES: Record<string, string> = {
  rfc: "Mismo RFC (determinístico)",
  folio_mercantil: "Mismo folio en el Registro Público de Comercio",
  nombre_exacto: "Nombre idéntico",
  nombre_similar: "Nombre similar",
};

type FiltroCerteza = "todos" | "confirmados" | "posibles";

/**
 * Cruce de listas — ¿quién aparece en el 69/69-B del SAT Y ADEMÁS está
 * sancionado por OFAC o bajo special measure de FinCEN?
 *
 * Dos niveles, deliberadamente separados: el cruce por RFC identifica al
 * mismo contribuyente (confirmado); el cruce por nombre puede ser un
 * homónimo y SIEMPRE se muestra como "posible coincidencia" a verificar.
 */
export function Cruce({ cliente }: { cliente: ClienteNexo }) {
  const [certeza, setCerteza] = useState<FiltroCerteza>("todos");
  const [q, setQ] = useState("");
  const [qAplicada, setQAplicada] = useState("");
  const [pagina, setPagina] = useState(0);
  const [resultado, setResultado] = useState<ResultadoCruce | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const porPagina = 50;

  useEffect(() => {
    let vivo = true;
    setResultado(undefined);
    vizCruce(cliente, {
      confirmado: certeza === "todos" ? null : certeza === "confirmados",
      q: qAplicada || null,
      limit: porPagina,
      offset: pagina * porPagina,
    })
      .then((r) => {
        if (vivo) setResultado(r);
      })
      .catch((e: unknown) => {
        if (vivo) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      vivo = false;
    };
  }, [cliente, certeza, qAplicada, pagina]);

  if (error) return <ErrorBox mensaje={error} />;

  return (
    <div>
      <div style={{ ...st.tarjeta }}>
        <h1 style={{ ...st.display, fontSize: 22, margin: "0 0 4px" }}>Cruce de listas</h1>
        <p style={{ fontSize: 13, color: C.muted, margin: "0 0 14px", lineHeight: 1.5 }}>
          Contribuyentes en las listas del SAT (artículo 69 y empresas que emiten facturas falsas) que
          también están en la lista de OFAC o bajo una medida especial de la Oficina del Tesoro contra
          el lavado. El cruce por RFC identifica al mismo contribuyente. El cruce por nombre puede ser
          otra persona con el mismo nombre y se marca como posible: verifica con RFC o folio en el
          Registro Público de Comercio antes de decidir.
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          {(["todos", "confirmados", "posibles"] as const).map((f) => (
            <button
              key={f}
              onClick={() => {
                setCerteza(f);
                setPagina(0);
              }}
              style={{
                ...st.btn,
                cursor: "pointer",
                ...(certeza === f ? { background: C.tielDark, color: "#fff", borderColor: C.tielDark } : {}),
              }}
            >
              {f === "todos" ? "Todos" : f === "confirmados" ? "Confirmados (RFC)" : "Posibles (nombre)"}
            </button>
          ))}
          <form
            style={{ display: "flex", gap: 6, flex: 1, minWidth: 240 }}
            onSubmit={(ev) => {
              ev.preventDefault();
              setQAplicada(q.trim());
              setPagina(0);
            }}
          >
            <input
              value={q}
              onChange={(ev) => {
                setQ(ev.target.value);
              }}
              placeholder="RFC o nombre…"
              style={{ ...st.input, flex: 1 }}
            />
            <button type="submit" style={{ ...st.btnPrim, cursor: "pointer" }}>Buscar</button>
          </form>
        </div>
      </div>

      {resultado === undefined ? <Cargando texto="Cruzando listas…" /> : (
        <div style={{ ...st.tarjeta }}>
          <p style={st.eyebrow}>
            {String(resultado.total)} coincidencias
            {resultado.ultimo_refresco ? ` · último refresco ${fecha(resultado.ultimo_refresco)}` : ""}
          </p>
          {resultado.filas.length === 0 ? (
            <p style={{ fontSize: 13, color: C.muted }}>
              Sin coincidencias con estos filtros. El cruce se recalcula a diario tras las ingestas.
            </p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={celdaTh}>Certeza</th>
                  <th style={celdaTh}>Lado SAT (listas del SAT)</th>
                  <th style={celdaTh}>Lado sancionado</th>
                  <th style={celdaTh}>Método</th>
                </tr>
              </thead>
              <tbody>
                {resultado.filas.map((c, i) => (
                  <tr key={c.id ?? i}>
                    <td style={{ ...celdaTd, whiteSpace: "nowrap" }}>
                      {c.confirmado ? (
                        <span style={{ color: C.red, fontWeight: 700, fontSize: 12.5 }}>CONFIRMADO</span>
                      ) : (
                        <span style={{ color: C.amber, fontWeight: 700, fontSize: 12.5 }}>Posible</span>
                      )}
                    </td>
                    <td style={celdaTd}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{c.nombre_sat ?? "(sin nombre publicado)"}</div>
                      <div style={{ color: C.muted, fontSize: 11.5 }}>
                        <span style={{ fontFamily: "ui-monospace, Consolas, monospace" }}>{c.rfc}</span>
                        {" · "}
                        {c.listas.map((l) => etiquetaListaSat(l.lista, l.categoria)).join(", ")}
                      </div>
                    </td>
                    <td style={celdaTd}>
                      <a
                        href={rutaAHash({ vista: "entidad", id: c.entidad_id })}
                        style={{ color: C.tiel, textDecoration: "none", fontWeight: 600, fontSize: 13 }}
                      >
                        {c.entidad_nombre ?? c.entidad_id}
                      </a>
                      <div style={{ color: C.muted, fontSize: 11.5 }}>{c.autoridades.map(autoridadEs).join(" + ")}</div>
                    </td>
                    <td style={{ ...celdaTd, color: C.muted, fontSize: 12.5 }}>
                      {METODO_ES[c.metodo] ?? c.metodo}
                      {c.metodo === "nombre_similar" ? ` (score ${String(c.score)})` : ""}
                      {c.metodo === "nombre_similar" && c.nombre_matcheado ? (
                        <div style={{ fontSize: 11.5 }}>vs. “{c.nombre_matcheado}”</div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {resultado.total > porPagina ? (
            <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
              <button
                style={{ ...st.btn, cursor: "pointer" }}
                disabled={pagina === 0}
                onClick={() => {
                  setPagina((p) => Math.max(0, p - 1));
                }}
              >
                ← Anterior
              </button>
              <span style={{ fontSize: 12.5, color: C.muted }}>
                Página {String(pagina + 1)} de {String(Math.ceil(resultado.total / porPagina))}
              </span>
              <button
                style={{ ...st.btn, cursor: "pointer" }}
                disabled={(pagina + 1) * porPagina >= resultado.total}
                onClick={() => {
                  setPagina((p) => p + 1);
                }}
              >
                Siguiente →
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
